import { useMemo } from "react";
import { useUrlState } from "@/hooks/useUrlState";

/**
 * Workstream C (2026-09) — Consultant Performance's ONE period selection,
 * kept in the URL via the shared useUrlState mechanism so a refresh or the
 * browser's Back button restores the exact custom range (never a reset to a
 * preset). Every Consultant Performance request (roster, no-data view,
 * snapshot history, generate, reviews, Quality citations, coaching
 * current/generate) is built from the SAME `periodQuery` string below —
 * never a separately-computed range per view.
 *
 * Keys are prefixed `cp_` because the Reports page already owns
 * `preset`/`days`/`date_from`/`date_to` for its other tabs on the same URL.
 * The backend (consultant_performance.resolve_performance_period) is the
 * authority and re-validates everything; the checks here only avoid
 * sending a request that is certain to be rejected and explain why inline.
 */
export const MAX_CUSTOM_RANGE_DAYS = 366;

export const PERIOD_OPTIONS = [
  ["today", "Today"], ["week", "This Week"], ["month", "This Month"], ["custom", "Custom date range"],
];

export const CONSULTANT_PERFORMANCE_URL_DEFAULTS = {
  cp_period: "week", cp_from: "", cp_to: "", cp_product: "all", cp_scope: "family", cp_page: 1, cp_nodata: false, cp_user: "",
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const istToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

export function validateCustomRange(from, to, today = istToday()) {
  if (!from || !to) return "Choose both a start date and an end date.";
  if (!ISO_DATE.test(from) || !ISO_DATE.test(to) || Number.isNaN(Date.parse(from)) || Number.isNaN(Date.parse(to))) {
    return "Enter valid dates.";
  }
  if (to < from) return "The end date is before the start date.";
  if (to > today) return "The end date cannot be in the future.";
  const days = Math.round((Date.parse(to) - Date.parse(from)) / 86400000) + 1;
  if (days > MAX_CUSTOM_RANGE_DAYS) {
    return `A custom range can cover at most ${MAX_CUSTOM_RANGE_DAYS} days (this one covers ${days}).`;
  }
  return null;
}

export function buildPeriodQuery(periodType, from, to) {
  const p = new URLSearchParams({ period_type: periodType });
  if (periodType === "custom") { p.set("start", from); p.set("end", to); }
  return p.toString();
}

export function useConsultantPerformanceUrlState() {
  const [state, setState] = useUrlState(CONSULTANT_PERFORMANCE_URL_DEFAULTS);
  const periodType = state.cp_period;
  const rangeError = periodType === "custom" ? validateCustomRange(state.cp_from, state.cp_to) : null;
  const periodQuery = useMemo(
    () => (rangeError ? null : buildPeriodQuery(periodType, state.cp_from, state.cp_to)),
    [rangeError, periodType, state.cp_from, state.cp_to]);
  return { state, setState, periodType, from: state.cp_from, to: state.cp_to, rangeError, periodQuery };
}
